import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { AuthInfo } from "../../ui/models/authInfo";
import { UserInfo } from "../../ui/models/userInfo";
export const AUTH_FEATURE_KEY = "auth";
export const LOGIN_URL = `auth/login`;
export const REFRESH_TOKEN_URL = `auth/RefreshToken`;

export interface SignInData {
  userName: string;
  password: string;
}
export interface RefreshTokenData {
  refreshToken: string;
  redirectUri?: string;
}
export interface LogoutData {
  redirectUri: string;
  urlLogout: string;
}

export interface AuthState {
  loadingStatus: "not loaded" | "loading" | "loaded" | "error";
  error: string | null | undefined;
  info: AuthInfo | null;
}

export const fetchAuth = createAsyncThunk(
  "sign-in",
  async (signInData: SignInData, { rejectWithValue }) => {
    try {
      const response = await axios.post<AuthInfo>(`${LOGIN_URL}`, signInData, {
        params: {
          mock: false,
        },
      });

      return response.data as AuthInfo;
    } catch (error: any) {
      if (error.response?.status === 400) {
        let responseError: AuthState = {
          loadingStatus: "error",
          error:
            "Usuario o Contraseña incorrectos. Por favor verifique sus datos y vuelva a intentar.",
          info: null,
        };
        return rejectWithValue(responseError);
      } else {
        let responseError: AuthState = {
          loadingStatus: "error",
          error:
            "The system could not respond at this moment. Please, try again later.",
          info: null,
        };
        return rejectWithValue(responseError);
      }
    }
  }
);

export const fetchRefreshToken = createAsyncThunk(
  "auth/refreshToken",
  async (refreshTokenData: RefreshTokenData, { rejectWithValue }) => {
    try {
      const response = await axios.post<AuthInfo>(
        `${REFRESH_TOKEN_URL}`,
        refreshTokenData,
        {
          params: {
            mock: false,
          },
        }
      );
      return response.data as AuthInfo;
    } catch (error: any) {
      if (error.response?.status === 401) {
        let responseError: AuthState = {
          loadingStatus: "error",
          error: "Token Expired",
          info: null,
        };
        return rejectWithValue(responseError);
      } else {
        let responseError: AuthState = {
          loadingStatus: "error",
          error:
            "The system could not respond at this moment. Please, try again later.",
          info: null,
        };
        return rejectWithValue(responseError);
      }
    }
  }
);

export const fetchLogout = createAsyncThunk(
  "auth/logout",
  async (logoutData: LogoutData, { rejectWithValue }) => {
    try {
      // window.setTimeout(() => {
      //   window.location.href = logoutData.urlLogout;
      // }, 1000);
      return {} as AuthInfo;
    } catch (error: any) {
      let responseError: AuthState = {
        loadingStatus: "error",
        error:
          "The system could not respond at this moment. Please, try again later.",
        info: null,
      };
      return rejectWithValue(responseError);
    }
  }
);

export const initialAuthState: AuthState = {
  loadingStatus: "not loaded",
  error: null,
  info: null,
};

export const authSlice = createSlice({
  name: AUTH_FEATURE_KEY,
  initialState: initialAuthState,
  reducers: {
    logout: (state) => {
      state.loadingStatus = "not loaded";
      state.error = null;
      state.info = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAuth.fulfilled, (state: AuthState, { payload }) => {
        state.loadingStatus = "loaded";
        state.error = "";
        state.info = payload as AuthInfo;
      })
      .addCase(fetchAuth.rejected, (state: AuthState, { payload }) => {
        state.loadingStatus = "error";
        state.error = (payload as AuthState)?.error;
      })
      .addCase(fetchAuth.pending, (state: AuthState) => {
        state.loadingStatus = "loading";
      })
      .addCase(fetchRefreshToken.fulfilled, (state: AuthState, { payload }) => {
        state.loadingStatus = "loaded";
        state.error = "";
        if (state.info) {
          state.info.accessToken = payload.accessToken;
          state.info.expiresIn = payload.expiresIn;
          state.info.refreshToken = payload.refreshToken;
        }
      })
      .addCase(fetchRefreshToken.rejected, (state: AuthState, { payload }) => {
        state.loadingStatus = "error";
        state.error = (payload as AuthState)?.error;
      })
      .addCase(fetchRefreshToken.pending, (state: AuthState) => {
        state.loadingStatus = "loading";
      })
      .addCase(fetchLogout.fulfilled, (state: AuthState) => {
        state.loadingStatus = "not loaded";
        state.error = null;
        state.info = null;
      })
      .addCase(fetchLogout.rejected, (state: AuthState, { payload }) => {
        state.loadingStatus = "error";
        state.error = (payload as AuthState)?.error;
      })
      .addCase(fetchLogout.pending, (state: AuthState) => {
        state.loadingStatus = "loading";
      });
  },
});

/*
 * Export reducer for store configuration.
 */
export const authReducer = authSlice.reducer;

export const authActions = authSlice.actions;

export const getAuthState = (rootState: any): AuthState =>
  rootState[AUTH_FEATURE_KEY];
